import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { fetchCustomers, formatMinor, formatTerms } from '@/api/commercial';
import type { CustomerTier } from '@/types/generated';

const TIERS: { value: CustomerTier | ''; label: string }[] = [
  { value: '', label: 'Any tier' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'standard', label: 'Standard' },
  { value: 'preferred', label: 'Preferred' },
  { value: 'strategic', label: 'Strategic' },
];

const TIER_COLOUR: Record<CustomerTier, 'default' | 'info' | 'warning' | 'success'> = {
  prospect: 'default',
  standard: 'info',
  preferred: 'warning',
  strategic: 'success',
};

/**
 * The customer list.
 *
 * `useInfiniteQuery` rather than `useQuery`, because the endpoint is
 * cursor-paginated — there is no total and no page number to render, so the
 * interaction is "load more" and the query hook has to match. Trying to drive
 * numbered pages off a cursor is where this goes wrong.
 */
export function CustomersPage() {
  const [search, setSearch] = useState('');
  const [tier, setTier] = useState<CustomerTier | ''>('');

  // Debounced, so typing does not fire a request per keystroke. 300ms is long
  // enough to coalesce a word and short enough not to feel laggy.
  const debouncedSearch = useDebounced(search, 300);

  const query = useInfiniteQuery({
    // The filters are part of the key: without them, changing a filter would
    // append to the previous result set instead of replacing it.
    queryKey: ['customers', { search: debouncedSearch, tier }],
    queryFn: ({ pageParam }) =>
      fetchCustomers({ search: debouncedSearch, tier: tier || undefined, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
  });

  const customers = useMemo(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  return (
    <Stack spacing={2}>
      <Typography variant="h5">Customers</Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          size="small"
          label="Search"
          placeholder="Name, code, or tax id"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ minWidth: 260 }}
        />

        <TextField
          size="small"
          select
          label="Tier"
          value={tier}
          onChange={(event) => setTier(event.target.value as CustomerTier | '')}
          sx={{ minWidth: 160 }}
        >
          {TIERS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {query.isError && <Alert severity="error">Could not load customers.</Alert>}

      <Card variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Tier</TableCell>
              <TableCell align="right">Credit limit</TableCell>
              <TableCell>Terms</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {query.isPending && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            )}

            {!query.isPending && customers.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {debouncedSearch || tier
                      ? 'No customers match those filters.'
                      : 'No customers yet.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {customers.map((customer) => (
              <TableRow key={customer.id} hover>
                <TableCell>{customer.code ?? '—'}</TableCell>
                <TableCell>
                  <Box
                    component={RouterLink}
                    to={`/customers/${customer.id}`}
                    sx={{ color: 'primary.main', textDecoration: 'none' }}
                  >
                    {customer.name}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={customer.tier} color={TIER_COLOUR[customer.tier]} />
                </TableCell>
                <TableCell align="right">
                  {formatMinor(customer.credit_limit_minor, customer.default_currency)}
                </TableCell>
                <TableCell>{formatTerms(customer.credit_terms_days)}</TableCell>
                <TableCell>
                  {/* The derived flag, not the raw status: "on hold" matters
                      because it blocks new quotes, and that is what to show. */}
                  <Chip
                    size="small"
                    variant="outlined"
                    label={customer.status}
                    color={customer.allows_new_business ? 'success' : 'warning'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {query.hasNextPage && (
        <Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => void query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}
    </Stack>
  );
}

/**
 * Value that lags its input, so a filter does not fire a request per keystroke.
 *
 * `useEffect`, not `useMemo`: useMemo is for derived values and never invokes the
 * function it returns, so a timer created there is never cleared — every keystroke
 * would leave a pending timeout, and setting state during render is a loop waiting
 * to happen.
 */
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);

    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}
